import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Icon from "../components/Icon";
import { vehicleImage } from "../data/content";
import { useSiteContent } from "../siteContent";

export default function Branches() {
  const { branches } = useSiteContent();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [state, setState] = useState("All");
  const [selectedId, setSelectedId] = useState(branches[0]?.id);
  const filters = useMemo(() => ["All", ...new Set(branches.map((branch) => branch.state))], [branches]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return branches.filter((branch) => {
      const stateOk = state === "All" || branch.state === state;
      const text = `${branch.name} ${branch.city} ${branch.state}`.toLowerCase();
      return stateOk && (!needle || text.includes(needle));
    });
  }, [query, state, branches]);

  useEffect(() => {
    if (!filtered.some((branch) => branch.id === selectedId) && filtered[0]) {
      setSelectedId(filtered[0].id);
    }
  }, [filtered, selectedId]);

  const selected = filtered.find((branch) => branch.id === selectedId) || filtered[0];

  const applySearch = (event) => {
    event.preventDefault();
    const next = query.trim();
    if (next) setSearchParams({ q: next });
    else setSearchParams({});
  };

  return (
    <div className="bg-white">
      <section className="bg-[#0B6A34] text-white">
        <div className="container-page py-14 sm:py-16">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#b7e7a8]">Branch locator</p>
          <h1 className="mt-3 max-w-3xl text-3xl font-bold leading-tight sm:text-5xl">
            Every Essaar branch, with address, phone and hours
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-white/85 sm:text-base">
            Search by city or filter by state. Call the branch before you visit, especially for door pickup and large consignments.
          </p>
        </div>
      </section>

      <section className="container-page py-10">
        <form onSubmit={applySearch} className="flex flex-col gap-3 lg:flex-row lg:items-end">
          <label className="min-w-0 flex-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-[#5d6b62]">Search by city or branch name</span>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="For example Coimbatore"
              className="mt-1.5 w-full rounded-md border border-[#c5d5c9] px-3 py-2.5 text-sm focus:border-[#0B6A34] focus:outline-none focus:ring-2 focus:ring-[#0B6A34]/20"
            />
          </label>
          <div className="flex flex-wrap gap-2">
            {filters.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setState(item)}
                className={`rounded-md px-3 py-2 text-sm font-semibold ${
                  state === item ? "bg-[#0B6A34] text-white" : "border border-[#c5d5c9] text-[#243028] hover:border-[#0B6A34]"
                }`}
              >
                {item}
              </button>
            ))}
          </div>
        </form>

        <p className="mt-6 text-sm font-semibold text-[#0B6A34]">
          {filtered.length} {filtered.length === 1 ? "branch" : "branches"}
        </p>

        <div className="mt-4 grid gap-6 lg:grid-cols-[1fr_320px]">
          <div className="space-y-4">
            {filtered.length === 0 && (
              <p className="rounded-xl border border-dashed border-[#c5d5c9] p-6 text-sm text-slate-600">
                No branch matches that search. Try another city, or send a service enquiry.
              </p>
            )}
            {filtered.map((branch) => {
              const active = selected?.id === branch.id;
              const mapHref =
                branch.latitude != null && branch.longitude != null
                  ? `https://www.google.com/maps?q=${branch.latitude},${branch.longitude}`
                  : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      `${branch.name} ${branch.address || ""} ${branch.state || ""}`
                    )}`;
              return (
                <article
                  key={branch.id}
                  onClick={() => setSelectedId(branch.id)}
                  className={`cursor-pointer rounded-xl border bg-white p-5 ${
                    active ? "border-[#0B6A34] ring-1 ring-[#0B6A34]" : "border-[#d5e4d9]"
                  }`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h2 className="text-lg font-bold text-[#16301f]">{branch.name}</h2>
                      <p className="mt-1 text-sm text-slate-500">{branch.type}</p>
                    </div>
                    <span className="rounded-full bg-[#eef6f0] px-3 py-1 text-xs font-semibold text-[#0B6A34]">
                      {branch.state}
                    </span>
                  </div>
                  <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-3">
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-[#5d6b62]">Address</dt>
                      <dd className="mt-1 text-slate-700">{branch.address}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-[#5d6b62]">Phone</dt>
                      <dd className="mt-1 text-slate-700">{branch.phone}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-[#5d6b62]">Hours</dt>
                      <dd className="mt-1 text-slate-700">{branch.hours}</dd>
                    </div>
                  </dl>
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <a
                      href={mapHref}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`Open map for ${branch.name}`}
                      title="Open map"
                      className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-[#0B6A34] text-white hover:bg-[#085628]"
                    >
                      <Icon name="map" className="h-5 w-5" />
                    </a>
                    <Link
                      to={`/service-enquiry?branch=${encodeURIComponent(branch.name)}`}
                      className="rounded-md border border-[#0B6A34] px-3 py-2 text-sm font-semibold text-[#0B6A34] hover:bg-[#eef6f0]"
                    >
                      Enquire at this branch
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>

          <aside className="h-fit overflow-hidden rounded-xl border border-[#d5e4d9] bg-[#f4f7f5] lg:sticky lg:top-32">
            <img src={vehicleImage} alt="" className="h-40 w-full object-cover" />
            <div className="p-4">
              <p className="text-sm font-semibold text-[#16301f]">
                {selected ? selected.name : "Branch map"}
              </p>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                A live map with one pin per branch will connect to this list. Selecting a card highlights that branch here.
              </p>
            </div>
          </aside>
        </div>

        <div className="mt-8 flex flex-col items-start justify-between gap-4 rounded-xl bg-[#f4f7f5] p-5 sm:flex-row sm:items-center">
          <div>
            <h2 className="text-lg font-bold text-[#16301f]">No branch in your town?</h2>
            <p className="mt-1 text-sm text-slate-600">
              Send a service enquiry with your pickup and delivery locations. We will tell you the nearest branch and how to book.
            </p>
          </div>
          <Link to="/service-enquiry" className="shrink-0 rounded-md bg-[#6fbf45] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#5eaa38]">
            Send a service enquiry
          </Link>
        </div>
      </section>
    </div>
  );
}
